import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Permiso } from 'models/permiso_model';
import {
  getPermisos,
  createPermiso,
  editPermiso,
  infoPermiso,
  aceptarRechazarPermiso,
  deletePermiso,
  sendEmail,
} from 'repositories/permiso_repository';

interface PermisoState {
  permisos: Permiso[];
  permiso: Permiso | null;
  loading: boolean;
  error: string | null;
}

const initialState: PermisoState = {
  permisos: [],
  permiso: null,
  loading: false,
  error: null,
};

export const cargarPermisos = createAsyncThunk<Permiso[], { estado?: string; estudianteId?: string } | undefined, { rejectValue: string }>(
  'permiso/cargarPermisos',
  async (filters = {}, { rejectWithValue }) => {
    try {
      return (await getPermisos(filters.estado, filters.estudianteId)) ?? [];
    } catch {
      return rejectWithValue('Error al cargar permisos');
    }
  },
);

export const agregarPermiso = createAsyncThunk(
  'permiso/agregarPermiso',
  async (data: Permiso, { rejectWithValue }) => {
    try {
      await createPermiso(data);
    } catch {
      return rejectWithValue('Error al crear permiso');
    }
  },
);

export const editarPermiso = createAsyncThunk(
  'permiso/editarPermiso',
  async ({ data, permisoId }: { data: Permiso; permisoId: string }, { rejectWithValue }) => {
    try {
      await editPermiso(data, permisoId);
    } catch {
      return rejectWithValue('Error al editar permiso');
    }
  },
);

export const masInfoPermiso = createAsyncThunk(
  'permiso/masInfoPermiso',
  async ({ data, permisoId }: { data: Permiso; permisoId: string }, { rejectWithValue }) => {
    try {
      await infoPermiso(data, permisoId);
    } catch {
      return rejectWithValue('Error al actualizar información del permiso');
    }
  },
);

export const responderPermiso = createAsyncThunk(
  'permiso/responderPermiso',
  async ({ data, permisoId }: { data: Permiso; permisoId: string }, { rejectWithValue }) => {
    try {
      await aceptarRechazarPermiso(data, permisoId);
    } catch {
      return rejectWithValue('Error al responder permiso');
    }
  },
);

export const eliminarPermiso = createAsyncThunk(
  'permiso/eliminarPermiso',
  async (id: string, { rejectWithValue }) => {
    try {
      await deletePermiso(id);
    } catch {
      return rejectWithValue('Error al eliminar permiso');
    }
  },
);

export const enviarCorreo = createAsyncThunk(
  'permiso/enviarCorreo',
  async (id: string, { rejectWithValue }) => {
    try {
      await sendEmail(id);
    } catch {
      return rejectWithValue('Error al enviar correo');
    }
  },
);

export const permisoSlice = createSlice({
  name: 'permiso',
  initialState,
  reducers: {
    changePermiso: (state, action: PayloadAction<Permiso | null>) => {
      state.permiso = action.payload;
    },
    setPermisos: (state, action: PayloadAction<Permiso[]>) => {
      state.permisos = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarPermisos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarPermisos.fulfilled, (state, action) => {
        state.loading = false;
        state.permisos = action.payload;
      })
      .addCase(cargarPermisos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { changePermiso, setPermisos } = permisoSlice.actions;
export default permisoSlice.reducer;
